import { useState } from "react";
import { theme, FONT_RUNE } from "./theme";
import { appBase, publicUrl } from "./route";
import { defaultVictoryConfig, type VictoryConfig, type VictoryLevel } from "../engine";
import { Instructions } from "./Instructions";
import { StoryPanel } from "./StoryPanel";
import { AccountChip } from "./account/AccountChip";

export type GameMode = "local" | "bot" | "online";

export interface GameOptions {
  victoryConfig: VictoryConfig;
  humanSide: "white" | "black";
}

// One-line hints for the picker. Full breakdowns of each victory level live in the
// Instructions panel — the picker just needs to make the choice obvious at a glance.
const VICTORY_LEVELS: { level: VictoryLevel; label: string; description: string }[] = [
  { level: "common", label: "Common", description: "Capture pieces totaling at least the threshold." },
  { level: "proper", label: "Proper", description: "Threshold + 4+ pieces in 2+ shapes." },
  { level: "greater", label: "Greater", description: "One progression of three pieces in enemy ranks." },
  { level: "royal", label: "Royal", description: "Two progressions formed among the same four pieces." },
  { level: "glorious", label: "Glorious", description: "All three progressions in the same four pieces." },
];

export function ModeSelect({
  onStart,
  onWatchTutorial,
}: {
  onStart: (mode: GameMode, opts: GameOptions) => void;
  onWatchTutorial: () => void;
}) {
  const [mode, setMode] = useState<GameMode>("bot");
  const [level, setLevel] = useState<VictoryLevel>("common");
  const [threshold, setThreshold] = useState(160);
  // Black moves first, so defaulting the human to Black means they get the opening (and most
  // beginners expect the first-mover seat).
  const [side, setSide] = useState<"white" | "black">("black");
  const [instructionsOpen, setInstructionsOpen] = useState(false);
  const [storyOpen, setStoryOpen] = useState(false);

  const onlineDisabled = false;

  const handleStart = () => {
    const cfg: VictoryConfig = {
      ...defaultVictoryConfig(),
      level,
      commonThreshold: threshold,
      // Proper Victory needs ≥4 captured pieces across ≥2 shapes in addition to the threshold;
      // the defaults zero these out (they're V1-irrelevant), so set them here when the player
      // explicitly picks Proper.
      properMinPieces: level === "proper" ? 4 : 0,
      properMinShapes: level === "proper" ? 2 : 0,
    };
    onStart(mode, { victoryConfig: cfg, humanSide: side });
  };

  return (
    <div style={container}>
      <div
        style={{
          position: "fixed",
          top: "calc(var(--safe-top) + 12px)",
          right: "calc(var(--safe-right) + 12px)",
          zIndex: 50,
        }}
      >
        <AccountChip />
      </div>
      <div
        style={{
          width: "100%",
          maxWidth: 460,
          // Cap to the available viewport (container handles safe-area padding) so the card
          // never overflows the screen. Inside, the content scrolls independently and the
          // Begin / secondary buttons stay docked as a sticky footer.
          maxHeight: "100%",
          display: "flex",
          flexDirection: "column",
          background: "rgba(26, 14, 8, 0.92)",
          border: `1px solid ${theme.boardEdge}`,
          borderRadius: 12,
          boxShadow: "0 10px 36px rgba(0, 0, 0, 0.55)",
        }}
      >
        <div
          style={{
            // Scrollable content region. Everything except the Begin / secondary buttons.
            flex: "1 1 auto",
            minHeight: 0,
            overflowY: "auto",
            padding: "22px 22px 8px",
          }}
        >
        {appBase() !== "" && (
          <a
            href="/"
            style={{
              display: "inline-block",
              color: theme.accentSoft,
              fontFamily: FONT_RUNE,
              fontSize: 11,
              letterSpacing: 2,
              textDecoration: "none",
              opacity: 0.75,
              marginBottom: 10,
            }}
          >
            ← AETHON GAMES
          </a>
        )}
        <div className="rune" style={{ color: theme.accentSoft, fontSize: 13, letterSpacing: 3, opacity: 0.8 }}>
          DE BELLO NUMERORUM
        </div>
        <h1
          style={{
            fontFamily: "var(--font-display)",
            fontWeight: 700,
            color: theme.parchment,
            fontSize: 36,
            margin: "2px 0 18px 0",
            lineHeight: 1.1,
            letterSpacing: 1,
          }}
        >
          Rithmomachia
        </h1>

        <Section title="Mode">
          <Row>
            <Choice selected={mode === "bot"} label="Vs Bot" onClick={() => setMode("bot")} />
            <Choice selected={mode === "local"} label="Local · Pass & Play" onClick={() => setMode("local")} />
            <Choice
              selected={mode === "online"}
              label={`Online${onlineDisabled ? " (soon)" : " (beta)"}`}
              onClick={() => !onlineDisabled && setMode("online")}
              disabled={onlineDisabled}
            />
          </Row>
        </Section>

        {mode === "bot" && (
          <Section title="Play as">
            <Row>
              <Choice selected={side === "black"} label="Black · moves first" onClick={() => setSide("black")} />
              <Choice selected={side === "white"} label="White" onClick={() => setSide("white")} />
            </Row>
          </Section>
        )}

        <Section title="Victory level">
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {VICTORY_LEVELS.map((v) => (
              <Choice
                key={v.level}
                selected={level === v.level}
                label={v.label}
                description={v.description}
                onClick={() => setLevel(v.level)}
                wide
              />
            ))}
          </div>
        </Section>

        {(level === "common" || level === "proper") && (
          <Section
            title="Threshold"
            tooltip="Each side's own captured-piece total needs to reach this to win — not the combined total of both sides. Both players race to the same target. 120 = quick game; 200 = long, strategic one."
          >
            <Row>
              {[120, 140, 160, 180, 200].map((t) => (
                <Choice
                  key={t}
                  selected={threshold === t}
                  label={String(t)}
                  onClick={() => setThreshold(t)}
                />
              ))}
            </Row>
          </Section>
        )}

        </div>

        <div
          style={{
            // Sticky footer — Begin and the secondary buttons stay visible no matter how
            // tall the content above grows.
            flexShrink: 0,
            padding: "12px 22px 18px",
            borderTop: `1px solid ${theme.boardEdge}`,
            background: "rgba(26, 14, 8, 0.92)",
          }}
        >
          <button onClick={handleStart} style={startButton}>
            Begin
          </button>

          <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
            <button onClick={onWatchTutorial} style={secondaryButton}>
              Watch tutorial game
            </button>
            <button onClick={() => setInstructionsOpen(true)} style={secondaryButton}>
              How to play
            </button>
            <button onClick={() => setStoryOpen(true)} style={{ ...secondaryButton, flex: "1 1 100%" }}>
              The story behind the game
            </button>
          </div>
        </div>
      </div>

      <Instructions
        open={instructionsOpen}
        onClose={() => setInstructionsOpen(false)}
        onWatchTutorial={onWatchTutorial}
        onJumpToGame={handleStart}
      />
      <StoryPanel open={storyOpen} onClose={() => setStoryOpen(false)} />
    </div>
  );
}

function Section({ title, tooltip, children }: { title: string; tooltip?: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div
        className="rune"
        style={{
          color: theme.boardLabel,
          fontSize: 11,
          letterSpacing: 2,
          marginBottom: 6,
          display: "flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        <span>{title.toUpperCase()}</span>
        {tooltip && <HelpBadge text={tooltip} />}
      </div>
      {children}
    </div>
  );
}

function HelpBadge({ text }: { text: string }) {
  const [shown, setShown] = useState(false);
  return (
    <span
      tabIndex={0}
      onMouseEnter={() => setShown(true)}
      onMouseLeave={() => setShown(false)}
      onFocus={() => setShown(true)}
      onBlur={() => setShown(false)}
      onClick={(e) => {
        e.stopPropagation();
        setShown((s) => !s);
      }}
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 14,
        height: 14,
        borderRadius: 999,
        border: `1px solid ${theme.boardLabel}`,
        color: theme.boardLabel,
        fontFamily: FONT_RUNE,
        fontSize: 9,
        letterSpacing: 0,
        cursor: "help",
        userSelect: "none",
        outline: "none",
      }}
      aria-label="more info"
      role="tooltip"
    >
      ?
      {shown && (
        <span
          style={{
            position: "absolute",
            top: "calc(100% + 8px)",
            left: 0,
            zIndex: 5,
            background: theme.bgElev,
            color: theme.parchment,
            border: `1px solid ${theme.accent}`,
            borderRadius: 6,
            padding: "8px 10px",
            fontFamily: 'var(--font-ui)',
            fontSize: 13,
            lineHeight: 1.5,
            letterSpacing: 0,
            width: 240,
            textTransform: "none",
            whiteSpace: "normal",
            textAlign: "left",
            boxShadow: "0 6px 18px rgba(0,0,0,0.5)",
            pointerEvents: "none",
          }}
        >
          {text}
        </span>
      )}
    </span>
  );
}

function Row({ children }: { children: React.ReactNode }) {
  return <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>{children}</div>;
}

interface ChoiceProps {
  selected: boolean;
  label: string;
  description?: string;
  onClick: () => void;
  disabled?: boolean;
  wide?: boolean;
}

function Choice({ selected, label, description, onClick, disabled, wide }: ChoiceProps) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        flex: wide ? "1 1 100%" : "1 1 auto",
        textAlign: "left",
        padding: description ? "12px 14px" : "11px 14px",
        minHeight: 44,
        background: selected ? theme.accent : theme.bgElev,
        color: selected ? theme.bg : theme.parchment,
        borderRadius: 6,
        border: `1px solid ${selected ? theme.accent : theme.inkSoft}`,
        fontFamily: FONT_RUNE,
        fontSize: 14,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.4 : 1,
      }}
    >
      <div>{label}</div>
      {description && (
        <div style={{ fontSize: 11, color: selected ? theme.bg : theme.boardLabel, marginTop: 2, opacity: 0.85 }}>
          {description}
        </div>
      )}
    </button>
  );
}

const container: React.CSSProperties = {
  width: "100%",
  height: "100%",
  // Stacks: dark-brown 65% overlay on top of the scholar photo so the imagery sets mood
  // without competing with the text and controls.
  backgroundImage: `linear-gradient(rgba(26, 14, 8, 0.65), rgba(26, 14, 8, 0.65)), url(${publicUrl("bg-scholar.jpg")})`,
  backgroundSize: "cover",
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
  backgroundColor: theme.bg,
  display: "flex",
  alignItems: "center", // vertically center the card; previously flex-start which clipped tall cards
  justifyContent: "center",
  // No outer scroll — the inner card region handles overflow, so Begin stays docked.
  overflow: "hidden",
  // Honor iOS notch + home indicator so the menu card never sits under either.
  padding:
    "calc(var(--safe-top) + 16px) calc(var(--safe-right) + 8px) calc(var(--safe-bottom) + 16px) calc(var(--safe-left) + 8px)",
};

const startButton: React.CSSProperties = {
  width: "100%",
  padding: "14px 16px",
  minHeight: 52,
  background: theme.accent,
  color: theme.bg,
  border: `1px solid ${theme.accent}`,
  borderRadius: 6,
  fontFamily: FONT_RUNE,
  fontSize: 18,
  letterSpacing: 2,
  cursor: "pointer",
  marginTop: 8,
};

const secondaryButton: React.CSSProperties = {
  flex: "1 1 140px",
  padding: "12px 14px",
  minHeight: 44,
  background: theme.bgElev,
  color: theme.parchment,
  border: `1px solid ${theme.inkSoft}`,
  borderRadius: 6,
  fontFamily: FONT_RUNE,
  fontSize: 13,
  letterSpacing: 1,
  cursor: "pointer",
};
